// prepareSearchPageMeta.js
'use strict';

import getMetaTagsForHelmet from '../resource/getMetaTagsForHelmet.js';
import generateSearchResultsPageJsonLd from '../resource/generateSearchResultsPageJsonLd.js';
import { getI18nInstance } from '../resource/i18n.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import {
  searchPageTDKTranslateKeys,
  searchType,
} from '../resource/searchConstants.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { BRANDING_NAME } from '../RemoteConfigKeys.js';

/**
 * Prepare meta data for search pages
 * @param {Object} options
 * @param {Object} options.state - Redux state
 * @param {string} options.query - Search query string
 * @param {string} options.type - Search type (all, flix, swagger, story, hashtag)
 * @param {string} options.category - Search category (optional, for swagger searches)
 * @param {string} options.hostname - Request hostname
 * @param {boolean} options.includeJsonLd - Whether to include JSON-LD data
 * @param {string} options.canonical - Canonical URL
 * @param {Array} options.alternates - Alternate language links (optional)
 * @returns {Promise<Array>} Meta tags array for Helmet
 */
const prepareSearchPageMeta = async ({
  state,
  query = '',
  type = searchType.ALL,
  hostname = 'swag.live',
  includeJsonLd = false,
  canonical = null,
  alternates = null,
}) => {
  // Get i18n instance
  const { i18n } = getI18nInstance();

  // Get TDK translation keys based on search type
  const tdkKeys =
    searchPageTDKTranslateKeys[type] || searchPageTDKTranslateKeys.default;

  // Build title and description with query (use SEARCH namespace, same as Next.js)
  const baseTitle = i18n.t(tdkKeys.title, { ns: TranslationNamespace.SEARCH });
  const baseDescription = i18n.t(tdkKeys.description, {
    ns: TranslationNamespace.SEARCH,
  });

  // Format title and description (following Next.js pattern)
  const queryPrefix = query ? `${query} - ` : '';
  const displayTitle = `${queryPrefix}${baseTitle}`;
  const displayDescription = `${queryPrefix}${baseDescription}`;

  // Get branding name from remote config
  const brandingName = getRemoteConfigData(state, BRANDING_NAME);

  // Prepare meta options
  const metaOptions = {
    title: displayTitle,
    description: displayDescription,
    keywords: query ? [query, type, 'search', 'SWAG'] : ['search', 'SWAG'],
    images: [],
    ogType: 'website',
    twitterCard: 'summary',
    hostname,
    alternates: {
      canonical,
      alternates,
    },
    url: canonical,
    siteName: brandingName,
  };

  // Add JSON-LD if requested
  if (includeJsonLd) {
    const jsonLdData = await generateSearchResultsPageJsonLd({
      query,
      type,
      url: canonical || `https://${hostname}/search/${type}`,
    });
    metaOptions.jsonLd = jsonLdData;
  }

  return getMetaTagsForHelmet(metaOptions);
};

export default prepareSearchPageMeta;
